import React from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../../lib/theme';
import { type CareRow } from './types';
import { fmtDate } from './helpers';
import { WidgetCard } from './WidgetCard';

/* ─── CareStatusDot ─────────────────────────────────────────────────────── */

export function CareStatusDot({ status, dark }: { status: number; dark: boolean }) {
  const { t } = useTranslation();
  const colors: Record<number, string> = { 0: '#2e90fa', 1: '#98a2b3', 2: '#12b76a', 3: '#f79009', 4: '#667085' };
  const labels: Record<number, string> = { 0: t('pflege.cares.status.new'), 1: t('pflege.cares.status.cancelled'), 2: t('pflege.cares.status.confirmed'), 3: t('pflege.cares.status.running'), 4: t('pflege.cares.status.completed') };
  const tc = getThemeColors(dark);
  const color = colors[status] ?? tc.muted;
  return <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: color + '22', color, flexShrink: 0, marginLeft: 8 }}>{labels[status] ?? `S${status}`}</span>;
}

/* ─── BetreuungenWidget (compact) ─────────────────────────────────────────── */

export function BetreuungenWidget({ dark, cares, loading, onShowAll, onEditCare }: { dark: boolean; cares: CareRow[]; loading: boolean; onShowAll: (e: React.MouseEvent) => void; onEditCare?: (care: CareRow) => void }) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const preview = cares.slice(0, 3);
  return (
    <WidgetCard dark={dark} title={t('pflege.detail.dashboard.betreuungen')} badge={cares.length || null} onShowAll={onShowAll} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')} loading={loading}>
      <div>
        {!loading && preview.length === 0 && (
          <div style={{ padding: '10px 14px', fontSize: 12, color: tc.muted, fontStyle: 'italic' }}>{t('pflege.cares.empty')}</div>
        )}
        {preview.map(c => (
          <div
            key={c.id}
            role="button"
            tabIndex={0}
            aria-label={`Betreuung: ${c.bk_name ?? `BK #${c.bk_user_id}`}`}
            onClick={(e) => { e.stopPropagation(); onEditCare?.(c); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onEditCare?.(c); } }}
            style={{ padding: '8px 14px', borderBottom: `1px solid ${tc.borderSoft}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: onEditCare ? 'pointer' : 'default' }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: tc.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.bk_name ?? `BK #${c.bk_user_id}`}</div>
              <div style={{ fontSize: 11, color: tc.muted }}>{fmtDate(c.startdatum)} – {c.enddatum ? fmtDate(c.enddatum) : '…'}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CareStatusDot status={c.status} dark={dark} />
              {onEditCare && <span style={{ fontSize: 10, color: tc.muted }}>✏</span>}
            </div>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}
